import type { Booking } from "@/types/Booking";

const WEEKDAY_NAMES: Record<string, string> = {
  SU: "Sunday",
  MO: "Monday",
  TU: "Tuesday",
  WE: "Wednesday",
  TH: "Thursday",
  FR: "Friday",
  SA: "Saturday",
};

const DAY_INDEX_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Format a stored time ("HHMM" or "HH:mm") as a friendly 12-hour clock, e.g. "10am", "2:30pm". */
export function formatTime12h(time: string): string {
  const clean = (time ?? "").replace(":", "");
  const hours = parseInt(clean.slice(0, 2), 10);
  const minutes = parseInt(clean.slice(2, 4), 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return time;
  const period = hours < 12 ? "am" : "pm";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return minutes === 0 ? `${hour12}${period}` : `${hour12}:${minutes.toString().padStart(2, "0")}${period}`;
}

function weekdayNames(booking: Booking): string[] {
  const rec = booking.recurrence ?? booking.recurrencePattern;
  const codes = rec?.byWeekday;
  if (codes && codes.length) {
    const names = codes.map((c) => WEEKDAY_NAMES[c]).filter(Boolean);
    if (names.length) return names;
  }
  // Fall back to the genesis date's weekday when byWeekday isn't set. `date` is stored as
  // UTC-midnight of the centre's day, so use getUTCDay — a browser-local getDay() reads the
  // wrong weekday for a viewer west of UTC.
  return [DAY_INDEX_NAMES[new Date(booking.date).getUTCDay()]];
}

function joinDays(days: string[]): string {
  if (days.length <= 1) return days[0] ?? "";
  return `${days.slice(0, -1).join(", ")} & ${days[days.length - 1]}`;
}

/**
 * Human-readable summary of a recurring booking's cadence for the cancel modal, e.g.
 * "Weekly, every Tuesday at 10am". Shown in place of a single (misleading) genesis date for
 * recurring bookings. Falls back to the genesis weekday when byWeekday is absent.
 */
export function describeBookingRecurrence(booking: Booking): string {
  const rec = booking.recurrence ?? booking.recurrencePattern;
  const frequency = rec?.frequency === "monthly" ? "Monthly" : "Weekly";
  const days = joinDays(weekdayNames(booking));
  return `${frequency}, every ${days} at ${formatTime12h(booking.startTime)}`;
}
